import { useEffect, useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import { ContactModalBody, StyledModal } from "../styles/Smb.styled";
import NumberFormat from "react-number-format";
import { FormError } from "../enrollment/FormError";
import { Button } from "../styles/Button.styled";
import { useNavigate } from "react-router-dom";
import {
  fetchSaveSmbStep,
  fetchSendSmsCode,
  fetchVerifySmsCode,
} from "../../api/smb";
import { css } from '@emotion/css';

interface GetStartedModalProps {
  show: boolean;
  handleClose: () => void;
}

interface VerificationResponse {
  status?: string;
  response?: {
    message?: string;
  };
}

const GetStartedModal = ({ show, handleClose }: GetStartedModalProps) => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState<string>("");
  const [isInvalidPhone, setIsInvalidPhone] = useState<boolean>(false);

  const [code, setCode] = useState<string>("");
  const [stage, setStage] = useState<"entry" | "verify">("entry");
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isInvalidCode, setIsInvalidCode] = useState<boolean>(false);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(0);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown]);

  const handleSendCode = async (): Promise<void> => {
    setIsInvalidPhone(false);
    setSmsError(null);
    const trimmed = phone.replace(/\D/g, "");
    if (trimmed.length < 10) {
      setIsInvalidPhone(true);
      return;
    }
    setIsSending(true);
    try {
      await fetchSendSmsCode({ type: "phone", contact: trimmed });
      setStage("verify");
      setCountdown(60);
    } catch (err: any) {
      setSmsError(err.message || "Failed to send code");
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyCode = async (): Promise<void> => {
    setIsInvalidCode(false);
    setSmsError(null);
    if (!code || code.length < 4) {
      setIsInvalidCode(true);
      return;
    }
    setIsVerifying(true);
    try {
      const res: VerificationResponse | undefined = await fetchVerifySmsCode({
        type: "phone",
        contact: phone.replace(/\D/g, ""),
        code,
      });
      if (res?.status !== "1") {
        setIsVerifying(false);
        throw new Error(res?.response?.message || "Invalid verification code");
      }
      fetchSaveSmbStep({
        Step: "contact number",
        StepData: { phone: phone.replace(/\D/g, "") },
      });
      navigate(`/smb/info?phone=${phone.replace(/\D/g, "")}`);
    } catch (err: any) {
      setSmsError(err.message || "Invalid verification code");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBack = (): void => {
    setCode("");
    setSmsError(null);
    setStage("entry");
  };

  return (
    <StyledModal
      size="lg"
      show={show}
      style={{ marginTop: "150px", }}
      onHide={() => {
        handleClose();
      }}
    >
      <div style={{ backgroundColor: "#015343", color: "white", borderRadius: "12px", }}>
        <Modal.Header closeButton>
          <Modal.Title >Ready to View Your Custom Quotes?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ContactModalBody>
            {stage === "entry" && (
              <>
                <div style={{ textAlign: "left", display: "flex", alignItems: "start", gap: "10px", }}>
                  <img src="/images/icon-contactmodal.png" width={32} className="mt-2" alt="" />
                  <div style={{ maxWidth: "352px" }}>
                    Please enter your phone number below so we can
                    verify you are a real person.
                  </div>
                </div>
                <div style={{ marginTop: '64px', textAlign: 'left', marginBottom: '4px' }}>Phone*</div>
                <div style={{ display: "flex", flexDirection: "row", gap: "10px", alignItems: "start", }} className={css` @media (max-width: 768px) {
    flex-direction: column !important;
    align-items: center;
    gap: 15px;
  }
`}>
                  <div style={{ width: '100%', maxWidth: '360px' }} className={css`
      @media (max-width: 768px) {
        max-width: 100%;
      }
    `}>
                    <Form.Group style={{marginBottom: 8}}>
                      <NumberFormat
                        format="### ### ####"
                        mask="_"
                        name="phone"
                        value={phone}
                        placeholder="Enter your mobile phone number"
                        onValueChange={(values) => {
                          setPhone(values.formattedValue);
                        }}
                        className={`form-control ${css`
          @media (max-width: 768px) {
            height: 45px;
            font-size: 16px;
            padding: 10px 15px;
          }
        `}`}
                      />
                    </Form.Group>
                    {isInvalidPhone && <FormError message={"Invalid phone number"} />}
                    {smsError && <FormError message={smsError} />}
                  </div>
                  <Button
                    onClick={handleSendCode}
                    disabled={isSending || countdown > 0}
                    className={css`
        @media (max-width: 768px) {
          width: 100%;
          max-width: 360px;
          min-height: 45px;
          font-size: 16px;
        }
      `}
                    style={{
                      width: '100%',
                      maxWidth: '360px'
                    }}
                  >
                    {isSending ? (
                      <Spinner
                        animation="border"
                        variant="light"
                        size="sm"
                      />
                    ) : countdown > 0 ? (
                      `Resend in ${countdown}`
                    ) : (
                      "Send me a code"
                    )}
                  </Button>
                </div>
              </>
            )}

            {stage === "verify" && (
              <>
                <p>Enter the 4-digit code we just sent to {phone}.</p>
                <Form.Group style={{ margin: "20px auto" }}>
                  <Form.Control
                    type="text"
                    maxLength={4}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Enter code"
                  />
                </Form.Group>
                {isInvalidCode && (
                  <FormError message="Please enter a valid verification code." />
                )}
                {smsError && <FormError message={smsError} />}
                <Button
                  variant="light"
                  onClick={handleBack}
                  disabled={isVerifying}
                  className="me-4"
                >
                  Back
                </Button>
                <Button onClick={handleVerifyCode} disabled={isVerifying}>
                  {isVerifying ? (
                    <Spinner
                      animation="border"
                      variant="light"
                      size="sm"
                      className="ms-2"
                    />
                  ) : (
                    "Verify & Continue"
                  )}
                </Button>
                <p style={{ marginTop: 24, fontSize: 14, fontWeight: 300 }}>
                  Didn't receive a code?{" "}
                  <button
                    onClick={handleSendCode}
                    disabled={countdown > 0}
                    style={{
                      color: "#30d886",
                      border: "none",
                      background: "none",
                    }}
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : "Resend"}
                  </button>
                </p>
              </>
            )}

          </ContactModalBody>
        </Modal.Body>
      </div>
    </StyledModal>
  );
};

export default GetStartedModal;